// Curated bike-park knowledge for graceful planner guidance.
//
// Why this exists: some lift-served bike parks (Morzine Pleney, Mayrhofen
// Penken, Les Gets) have their gravity descents ABSENT from OpenStreetMap, so
// the routing graph is thin or empty from those lift tops and a plan there
// reads as broken. Per the mission, the planner degrades to sector-level
// guidance rather than failing: where we know an area is a real bike park, we
// surface an honest card ("a lift-served bike park, N runs, ride from the top,
// official map: <link>") instead of an empty itinerary.
//
// This file holds FACTS ONLY - park names, run counts, grade ranges, serving
// lifts, and links to the resort's own trail map - curated from official
// sources (each entry carries its source + verified date). It contains NO
// trail geometry: drawing the missing runs is an OpenStreetMap contribution,
// not something to fabricate or trace from a restricted source. See
// docs/features/mtb-trail-tagging-investigation.md.
//
// Pure data + pure helpers, client-importable (no DB, no framework).

export type BikeParkRun = {
  name: string;
  /** green | blue | red | black | double-black, or the resort's own wording. */
  grade: string;
};

export type BikeParkInfo = {
  /** condition_locations slug this park belongs to (a planner area member). */
  slug: string;
  /** The park's name as the resort spells it. */
  name: string;
  /** The lift(s)/sector(s) that serve it (match area_lifts names where they do). */
  servingLifts: string[];
  /** Advertised marked-run count, or null when no official figure is published. */
  runCount: number | null;
  /** e.g. "green to double-black". */
  gradeRange: string;
  /** The resort's own summer trail-map / bike-park page. */
  trailMapUrl: string;
  /** A few famous named runs, when officially listed. */
  marqueeRuns: BikeParkRun[];
  /** Official source the figures came from. */
  source: string;
  /** YYYY-MM-DD this was verified. */
  verifiedOn: string;
};

// Keyed by condition_locations slug. Populated from the 2026-06-13 gap-park
// research (official resort trail maps); see the curation note above.
export const BIKE_PARKS: Record<string, BikeParkInfo> = {
  morzine: {
    slug: "morzine",
    name: "Morzine Bike Park (Le Pléney)",
    servingLifts: ["Pléney", "Charniaz Express", "Super-Morzine"],
    runCount: 10,
    gradeRange: "blue to black",
    trailMapUrl:
      "https://www.ski-morzine.com/wp-content/uploads/2026/04/MorzineVTT-Z8-396x656-ins2026-2.pdf",
    marqueeRuns: [
      { name: "Mainline", grade: "black" },
      { name: "Woodride", grade: "black" },
      { name: "Super 8", grade: "red" },
      { name: "Back to Morzine", grade: "red" },
      { name: "Chevreuil", grade: "blue" },
      { name: "Atray", grade: "blue" },
    ],
    source: "https://en.portesdusoleil.com/in-summer/our-5-bikeparks/bikepark-morzine/",
    verifiedOn: "2026-06-13",
  },
  "les-gets": {
    slug: "les-gets",
    name: "Les Gets Bikepark",
    servingLifts: ["Chavannes Express", "Nauchets Express", "Mont Chéry"],
    runCount: 17,
    gradeRange: "green to double-black",
    trailMapUrl: "https://www.lesgets.bike/wp-content/uploads/2025/05/SoleGets-PlanVTTE25-Web.pdf",
    marqueeRuns: [
      { name: "Free Ride du Canyon", grade: "black" },
      { name: "Dans l'Gaz", grade: "black" },
      { name: "DH Noire", grade: "black" },
      { name: "Ellipse Line", grade: "double-black" },
      { name: "Airlines", grade: "double-black" },
      { name: "Piste des Écureuils", grade: "green" },
    ],
    source: "https://www.lesgets.bike/en/bike-park/",
    verifiedOn: "2026-06-13",
  },
  avoriaz: {
    slug: "avoriaz",
    name: "Avoriaz Bike Park",
    servingLifts: ["Proclou", "Zore", "Séraussaix"],
    runCount: 19,
    gradeRange: "green to black",
    trailMapUrl: "https://www.avoriaz.com/en/discover/maps--guide/avoriaz-mtb-maps/",
    marqueeRuns: [
      { name: "Hattock", grade: "black" },
      { name: "Coupe de France", grade: "black" },
      { name: "Da Trail", grade: "red" },
      { name: "Walibi", grade: "red" },
      { name: "Cap Canaveral", grade: "blue" },
      { name: "Fourmiz", grade: "green" },
    ],
    source: "https://www.avoriaz.com/en/discover/maps--guide/avoriaz-mtb-maps/",
    verifiedOn: "2026-06-13",
  },
  chatel: {
    slug: "chatel",
    name: "Châtel Bike Park",
    servingLifts: ["Pierre Longue", "Rochassons"],
    runCount: 21,
    gradeRange: "green to black",
    trailMapUrl: "https://www.chatel.com/content/uploads/2026/06/Plan-du-bike-park-2026.pdf",
    marqueeRuns: [
      { name: "Vink", grade: "green" },
      { name: "Panoramic", grade: "blue" },
      { name: "Serpentine", grade: "blue" },
      { name: "Vorachatak", grade: "red" },
      { name: "Dré dans l'pentu", grade: "black" },
      { name: "Air Voltage", grade: "black" },
    ],
    source: "https://www.chatel.com/en/bikepark/",
    verifiedOn: "2026-06-13",
  },
  mayrhofen: {
    slug: "mayrhofen",
    name: "Mayrhofen Bikepark (Penken)",
    servingLifts: ["Penkenbahn", "Kombibahn Penken"],
    runCount: 2,
    gradeRange: "red to black",
    trailMapUrl: "https://www.mayrhofen.at/en/stories/mountopolis-mountainbiking",
    marqueeRuns: [
      { name: "Himmelfahrt", grade: "red" },
      { name: "Höllenritt", grade: "black" },
    ],
    source: "https://www.mayrhofen.at/en/stories/mountopolis-mountainbiking",
    verifiedOn: "2026-06-13",
  },
  laax: {
    slug: "laax",
    name: "Laax Bikepark",
    servingLifts: ["FlemXpress Flims - Foppa", "LAAX - Crap Sogn Gion"],
    runCount: 7,
    gradeRange: "green to black",
    trailMapUrl: "https://www.laax.com/en/biking",
    marqueeRuns: [
      { name: "Never End Trail", grade: "black" },
      { name: "Runca Trail", grade: "red" },
      { name: "Segnes Trail", grade: "red" },
      { name: "Nagens Trail", grade: "red" },
      { name: "Green Valley Trail", grade: "blue" },
      { name: "Foppa Trail", grade: "green" },
    ],
    source: "https://www.flimslaax.com/en/biken/bike-trails",
    verifiedOn: "2026-06-14",
  },
  verbier: {
    slug: "verbier",
    name: "Verbier Bikepark",
    servingLifts: ["Châble - Verbier", "Médran 2", "Chaux-Express"],
    runCount: 9,
    gradeRange: "green to black",
    trailMapUrl: "https://verbierbikepark.ch/activites_dh_en.php",
    marqueeRuns: [
      { name: "Woooohhh", grade: "black" },
      { name: "Bortabitche", grade: "red" },
      { name: "Tsopu", grade: "blue" },
      { name: "Chôtatai", grade: "blue" },
      { name: "Tsenelle", grade: "green" },
    ],
    source: "https://www.verbier.ch/en/discover/explore-activities/sports-activities/the-whole-world-of-cycling/bike-park-and-downhill-slopes/",
    verifiedOn: "2026-06-14",
  },
  "alpe-dhuez": {
    slug: "alpe-dhuez",
    name: "Bike Park Alpe d'Huez",
    servingLifts: ["Pic Blanc 1", "Marmottes 1", "Signal"],
    runCount: 16,
    gradeRange: "green to black",
    trailMapUrl: "https://skipass.alpedhuez.com/ete/en/hiking-mtb-trail-map/",
    marqueeRuns: [
      { name: "Megavalanche", grade: "black" },
      { name: "Forêt de l'Ours", grade: "black" },
      { name: "La Mad'Oz", grade: "red" },
      { name: "Va Rouler l'Agneau", grade: "green" },
      { name: "Marmot'land", grade: "green" },
    ],
    source: "https://www.alpedhuez.com/en/bike-park-alpe-dhuez-2/",
    verifiedOn: "2026-06-14",
  },
  schladming: {
    slug: "schladming",
    name: "Bikepark Schladming (Planai)",
    servingLifts: ["Planaibahn I", "Planai West I"],
    runCount: 11,
    gradeRange: "green to black",
    trailMapUrl: "https://www.planai.at/en/activities/bikepark-schladming",
    marqueeRuns: [
      { name: "Worldcup Downhill", grade: "black" },
      { name: "Pro Downhill", grade: "black" },
      { name: "Jack Lumber Trail", grade: "black" },
      { name: "99 Jumpline", grade: "black" },
      { name: "Peak Flowline", grade: "green" },
    ],
    source: "https://www.planai.at/en/activities/bikepark-schladming",
    verifiedOn: "2026-06-14",
  },
  kronplatz: {
    slug: "kronplatz",
    name: "Kronplatz Bike Park",
    servingLifts: ["Kronplatz 2000", "Olang I", "Olang II", "Piz de Plaies"],
    runCount: 18,
    gradeRange: "blue to black",
    trailMapUrl: "https://www.kronplatz.com/en/the-kronplatz/activity/kronplatzbikepark/trails",
    marqueeRuns: [
      { name: "Hans Trail", grade: "black" },
      { name: "Alex Trail", grade: "black" },
      { name: "Herrnsteig Trail", grade: "red" },
      { name: "Gassl Trail", grade: "red" },
      { name: "Furcia Trail", grade: "red" },
      { name: "Dragon Trail", grade: "blue" },
    ],
    source: "https://www.kronplatz.com/en/the-kronplatz/activity/kronplatzbikepark",
    verifiedOn: "2026-06-14",
  },
  kitzbuhel: {
    slug: "kitzbuhel",
    name: "360° Flow Trails Kitzbühel",
    servingLifts: ["Hahnenkammbahn", "Fleckalmbahn", "Gaisberg"],
    runCount: 7,
    gradeRange: "blue to black",
    trailMapUrl: "https://www.kitzski.at/en/hiking-biking/kitzbike-trails.html",
    marqueeRuns: [
      { name: "Gaisberg Trail", grade: "black" },
      { name: "Fleckalm Trail", grade: "red" },
      { name: "Lisi Osl Trail", grade: "red" },
      { name: "Streiteck Trail", grade: "red" },
      { name: "Hahnenkamm Trail", grade: "blue" },
      { name: "Sonnenrast Trail", grade: "blue" },
    ],
    source: "https://www.kitzbuehel.com/sport/biken/single-trails/",
    verifiedOn: "2026-06-14",
  },
  cervinia: {
    slug: "cervinia",
    name: "Bike Park del Cervino (Matterhorn)",
    servingLifts: ["Plan Maison", "Cime Bianche Laghi"],
    runCount: 12,
    gradeRange: "green to red",
    trailMapUrl: "https://www.cervinia.it/en/cosa-fare/bike-park",
    marqueeRuns: [
      { name: "Napo Trail", grade: "red" },
      { name: "Jamais Moll Trail", grade: "red" },
      { name: "Cime Bianche Trail", grade: "red" },
      { name: "Plan Maison Trail 1A", grade: "red" },
    ],
    source: "https://www.cervinia.it/en/cosa-fare/bike-park",
    verifiedOn: "2026-06-14",
  },
  "les-houches": {
    slug: "les-houches",
    name: "Les Houches Bike Park (Le Prarion)",
    servingLifts: ["Prarion"],
    runCount: 3,
    gradeRange: "blue to black",
    trailMapUrl: "https://www.montblancnaturalresort.com/en/page/mountain-biking",
    marqueeRuns: [
      { name: "Chamois", grade: "black" },
      { name: "Sherwoody", grade: "black" },
      { name: "Bouquetins", grade: "red" },
      { name: "Sauce Myrtille", grade: "red" },
      { name: "Alpages Respect", grade: "blue" },
      { name: "Saucette", grade: "blue" },
    ],
    source: "https://en.chamonix.com/activities/summer/mountain-bike/",
    verifiedOn: "2026-06-14",
  },
  "cortina-dampezzo": {
    slug: "cortina-dampezzo",
    name: "Cortina Bike Park (Dolomiti)",
    servingLifts: ["Piè Tofana - Duca d'Aosta", "Lacedel-Socrepes", "Duca d'Aosta - Forcella Pomedes"],
    runCount: 17,
    gradeRange: "green to black",
    trailMapUrl: "https://www.cortinabikeland.com/en/esplora-il-bike-park",
    marqueeRuns: [
      { name: "Krampus", grade: "black" },
      { name: "Lucifer", grade: "black" },
      { name: "T-Rex", grade: "black" },
      { name: "Tofana Super Trail", grade: "red" },
      { name: "Over The Top", grade: "red" },
      { name: "Tofanina", grade: "red" },
    ],
    source: "https://www.cortinabikeland.com/en/esplora-il-bike-park",
    verifiedOn: "2026-06-14",
  },
  "selva-val-gardena": {
    slug: "selva-val-gardena",
    name: "Trail Arena Val Gardena",
    servingLifts: ["Ciampinoi", "Dantercepies I", "Piz Seteur 1"],
    runCount: 6,
    gradeRange: "green to black",
    trailMapUrl: "https://www.val-gardena.com/en/summer/mountain-bike-holiday-dolomites/trail-arena-val-gardena/",
    marqueeRuns: [
      { name: "Freeride Ciampinoi", grade: "black" },
      { name: "Jump Line", grade: "red" },
      { name: "Cir Tiera", grade: "blue" },
      { name: "Family Line", grade: "blue" },
      { name: "Paravis", grade: "green" },
    ],
    source: "https://www.val-gardena.com/en/summer/mountain-bike-holiday-dolomites/trail-arena-val-gardena/",
    verifiedOn: "2026-06-14",
  },
};

/**
 * Curated bike parks for the planner area, given its member slugs (entry +
 * linked members). Returns an empty array when none of the area's members has
 * curated bike-park data, so callers can simply check `.length`.
 */
export function bikeParksForArea(memberSlugs: readonly string[]): BikeParkInfo[] {
  const seen = new Set<string>();
  const out: BikeParkInfo[] = [];
  for (const slug of memberSlugs) {
    const park = BIKE_PARKS[slug];
    if (park && !seen.has(park.slug)) {
      seen.add(park.slug);
      out.push(park);
    }
  }
  return out;
}

/** One-line attribution for a park's figures (shown under the guidance card). */
export function bikeParkAttribution(park: BikeParkInfo): string {
  let host = park.source;
  try {
    host = new URL(park.source).hostname.replace(/^www\./, "");
  } catch {
    // non-URL source string: show it verbatim
  }
  return `Trail details from ${host}`;
}
